import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';

/**
 * Specifies the size of the loading indicator spinner.
 *
 * @remarks
 * Valid sizes are 'lg' for large, 'md' for medium (default), and 'sm' for small.
 * Use these values to scale the spinner proportionally to your layout.
 *
 * @example
 *
 * ```typescript
 * type SizeOption = 'lg' | 'md' | 'sm';
 * ```
 */
export type LoadingIndicatorSize = 'lg' | 'md' | 'sm';

/**
 * Displays a customizable loading spinner with optional status message and background overlay.
 *
 * @remarks
 * The LoadingIndicatorComponent provides a flexible loading indicator that can be displayed
 * with or without padding, with or without a background overlay, and with an optional delay
 * before showing. The component uses OnPush change detection strategy for optimal performance.
 * It includes accessibility features such as role="status" and an i18n-compliant aria-label.
 *
 * The component's visibility can be controlled through the `showDelay` input, which delays
 * the display of the indicator by a specified number of milliseconds. This is useful for
 * preventing indicator flicker during quick operations.
 *
 * @example
 *
 * ```html
 * <!-- Loading indicator with custom text and small size -->
 * <bb-loading-indicator-ui
 *   text="Loading Accounts"
 *   loaderSize="sm"
 * ></bb-loading-indicator-ui>
 * ```
 */
@Component({
  selector: 'bb-loading-indicator-ui',
  templateUrl: './loading-indicator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoadingIndicatorComponent implements OnInit {
  /**
   * Optional text to display below the loading spinner.
   *
   * @remarks
   * When provided, the text appears beneath the animated spinner in the loading indicator.
   * The component supports i18n for internationalization of this text.
   *
   * @example
   *
   * ```html
   * <bb-loading-indicator-ui
   *   text="Loading Accounts"
   * ></bb-loading-indicator-ui>
   * ```
   */
  @Input() text?: string;

  /**
   * Controls the size of the loading spinner.
   *
   * @remarks
   * Accepts 'lg' (large), 'md' (medium), or 'sm' (small) values.
   * Defaults to 'md' if not specified. The size affects the diameter of the
   * animated circle and scales proportionally to different layout contexts.
   *
   * @example
   *
   * ```html
   * <!-- Small loading indicator in a button -->
   * <bb-loading-indicator-ui loaderSize="sm"></bb-loading-indicator-ui>
   * ```
   */
  @Input() loaderSize: LoadingIndicatorSize = 'md';

  /**
   * Delay in milliseconds before the loading indicator becomes visible.
   *
   * @remarks
   * Defaults to 0 (immediate display). Use this to prevent indicator "flash" when
   * operations complete quickly. For example, a 500ms delay prevents the spinner from
   * appearing during sub-500ms operations, improving perceived performance.
   *
   * @example
   *
   * ```html
   * <!-- Loading indicator with 500ms delay to prevent brief flash -->
   * <bb-loading-indicator-ui [showDelay]="500"></bb-loading-indicator-ui>
   * ```
   */
  @Input() showDelay = 0;

  /**
   * Determines whether a gray background overlay is displayed behind the spinner.
   *
   * @remarks
   * Defaults to false (no background). When true, displays a gray background container
   * with CSS class 'bb-loading-indicator--with-background', useful for creating a
   * semi-transparent overlay effect over content beneath the indicator.
   *
   * @example
   *
   * ```html
   * <!-- Loading indicator with semi-transparent background overlay -->
   * <bb-loading-indicator-ui [hasBackground]="true"></bb-loading-indicator-ui>
   * ```
   */
  @Input() hasBackground = false;

  /**
   * Determines whether the spinner displays inline without default spacing.
   *
   * @remarks
   * Defaults to false (uses default padding and margin). When true, the spinner displays
   * inline with minimal spacing, useful for embedding the indicator within other components
   * or for constrained layout contexts such as buttons or compact containers.
   *
   * @example
   *
   * ```html
   * <!-- Inline spinner in load button (no padding/margin) -->
   * <button class="bb-load-button">
   *  <bb-loading-indicator-ui
   *    class="bb-load-button__loading-indicator"
   *    [inline]="true"
   *  ></bb-loading-indicator-ui>
   * </button>
   * ```
   */
  @Input() inline = false;

  /**
   * Internal state flag indicating whether the loading state should be displayed.
   *
   * @remarks
   * Initially false, becomes true after the showDelay timeout completes.
   * Controls the visibility of the loading indicator in the template through
   * control flow conditions.
   */
  showLoadingState = false;

  constructor(private readonly ref: ChangeDetectorRef) {}

  /**
   * Initializes the loading indicator display after the specified delay.
   *
   * @remarks
   * Triggered automatically by Angular's lifecycle. Sets `showLoadingState` to true
   * after waiting for the `showDelay` duration, then marks the component for check
   * to ensure change detection runs (important with OnPush strategy).
   */
  ngOnInit(): void {
    setTimeout(() => {
      this.showLoadingState = true;
      this.ref.markForCheck();
    }, this.showDelay);
  }
}
